import { appPaths, AUTH_ROUTE_PREFIX } from "@/constants/paths";
import { resetSessionStores } from "@/hooks/session-storage/create-session-store";
import { queryClient } from "@/queries/query-client";
import { withLoadedMixpanel } from "@/services/mix-panel/mix-panel-lazy";
import { beginLogout, clearAuthTokens } from "@/utils/auth-storage";

/**
 * @description One teardown for every way a session can end — the user signing
 * out, the idle timeout, or a refresh token the server refuses.
 *
 * The app must never reload the document to get back to login: a reload on
 * `/auth/login` repaints the very same page and reads as "it logged me out
 * twice". Instead the router is asked to replace the current entry, so the
 * transition is a single, in-app navigation.
 *
 * @author [Rajkumar Rathod]
 */

/**
 * The router lives inside React while the axios interceptor does not, so the
 * navigate function is registered once by RootLayout and reused from anywhere.
 */
type LoginRedirect = () => void;

let redirect: LoginRedirect | null = null;

export const setLoginRedirect = (fn: LoginRedirect | null) => {
  redirect = fn;
};

export const isOnAuthRoute = () =>
  window.location.pathname.startsWith(AUTH_ROUTE_PREFIX);

/**
 * Wipes everything scoped to the signed-in user. Storage first, then the
 * in-memory mirrors of it, so nothing can rehydrate from a stale key.
 */
export const clearSession = () => {
  withLoadedMixpanel((mixpanel) => mixpanel.resetUser());
  clearAuthTokens();
  sessionStorage.clear();
  resetSessionStores();
  queryClient.clear();
};

/**
 * Sends the user to login without reloading the document. Falls back to a
 * history replace only when the router has not mounted yet (an interceptor can
 * fire during the very first render).
 */
export const redirectToLogin = () => {
  if (isOnAuthRoute()) return;

  if (redirect) {
    redirect();
    return;
  }

  window.location.replace(appPaths.login);
};

/**
 * Session ended on the server's terms (refresh token rejected). Latches the
 * logout flag first so any other in-flight 401 falls through quietly instead
 * of starting a second teardown.
 */
export const expireSession = () => {
  beginLogout();
  clearSession();
  redirectToLogin();
};
